import { Component, type ReactNode } from 'react'

import { RetryNotice } from '@/ui/retry-notice'

export function PageErrorFallback() {
  return (
    <RetryNotice
      className="p-6"
      message="We couldn't load this page."
      onRetry={() => window.location.reload()}
    />
  )
}

type AppErrorBoundaryProps = {
  children: ReactNode
}

type AppErrorBoundaryState = {
  hasError: boolean
}

export function ErrorFallback() {
  return (
    <dialog
      ref={(dialog) => {
        if (dialog && !dialog.open) {
          dialog.showModal()
          dialog.querySelector('button')?.focus()
        }
      }}
      role="alertdialog"
      aria-labelledby="app-error-title"
      aria-describedby="app-error-description"
      onCancel={(event) => event.preventDefault()}
      className="bg-background text-foreground fixed inset-0 m-0 h-screen max-h-none w-screen max-w-none border-0 p-6"
    >
      <div className="flex h-full flex-col items-center justify-center gap-2 text-center">
        <h1
          id="app-error-title"
          className="font-heading text-foreground text-base font-medium"
        >
          Something went wrong
        </h1>
        <p id="app-error-description" className="text-muted-foreground text-sm">
          We couldn&apos;t load this page.
        </p>
        <button
          type="button"
          className="text-foreground text-sm underline underline-offset-4 focus-visible:outline-2 focus-visible:outline-offset-4"
          onClick={() => window.location.reload()}
        >
          Reload page
        </button>
      </div>
    </dialog>
  )
}

export class AppErrorBoundary extends Component<
  AppErrorBoundaryProps,
  AppErrorBoundaryState
> {
  state: AppErrorBoundaryState = { hasError: false }

  static getDerivedStateFromError(): AppErrorBoundaryState {
    return { hasError: true }
  }

  render() {
    return this.state.hasError ? <ErrorFallback /> : this.props.children
  }
}
